import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get } from "@/api/api";
import { useSuspenseQuery } from "@tanstack/react-query";
import { FeatureFlagContext, FeatureFlagResponse } from "./types";

/**
 * Query key factory for feature flags
 */
export const featureFlags = createQueryKeys('feature-flags', {
  check: (
    flagName: string,
    context: FeatureFlagContext,
  ) => ({
    queryKey: [flagName, context],
    queryFn: async () => {
      // Build query parameters based on the provided context
      const params = new URLSearchParams();
      params.append('flag_name', flagName);
      
      if (context.installationId) {
        params.append('installation_id', context.installationId.toString());
      } else {
        if (context.organizationId) {
          params.append('organization_id', context.organizationId.toString());
        }
        if (context.equipmentModelId) {
          params.append('equipment_model_id', context.equipmentModelId.toString());
        }
      }
      
      return await get<FeatureFlagResponse>(`feature-flags/check/?${params.toString()}`);
    },
  })
});

/**
 * Hook for checking if a feature flag is active
 * 
 * @param flagName The name of the flag to check
 * @param context The context for the feature flag (installation, organization, equipment model)
 * @returns Query result with the active status of the flag
 */
export const useFeatureFlag = (flagName: string, context: FeatureFlagContext) => {
  return useSuspenseQuery(featureFlags.check(flagName, context));
}

/**
 * Simpler hook that returns just the active status of a feature flag
 * 
 * @param flagName The name of the flag to check
 * @param context The context for the feature flag (installation, organization, equipment model)
 * @returns boolean indicating if the flag is active
 */
export const useIsFeatureFlagActive = (flagName: string, context: FeatureFlagContext) => {
  const { data } = useFeatureFlag(flagName, context);
  return data.active;
}